import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { ICol, IRendererConfig, GridEvents, IRow, IEditor } from "../../types";
import { Calendar, CalendarEvents, stringToDate } from "@dhx/ts-calendar";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { Position } from "@dhx/ts-common/html";

export class DateEditor implements IEditor {
	protected _handlers: { [key: string]: (...args) => void };
	protected _cell: { row: IRow, col: ICol };
	protected _config: IRendererConfig;
	protected _input: HTMLInputElement;
	private _calendar: Calendar;
	private _popup: Popup;
	private _value: string;

	constructor(row: IRow, col: ICol, config: IRendererConfig) {
		this._config = config;
		this._cell = { row, col };
		this._calendar = new Calendar(null, { dateFormat: col.dateFormat });
		const value = this._cell.row[this._cell.col.id];
		const dateFormat = this._calendar.config.dateFormat;
		if (stringToDate(value, dateFormat, true) || value instanceof Date) {
			this._calendar.setValue(value);
			this._value = this._calendar.getValue();
			this._cell.row[this._cell.col.id] = this._value;
		}
		this._popup = new Popup({ css: "dhx_widget--bordered" });
		this._popup.attach(this._calendar);
		this._calendar.events.on(CalendarEvents.change, () => {
			this.endEdit(false, true);
		});
		this._popup.events.on(PopupEvents.afterHide, () => {
			this.endEdit();
		});
		this._initHandlers();
	}
	endEdit(withoutSave?: boolean, calendarChange?: boolean) {
		if (!this._handlers) {
			return;
		}
		const dateFormat = this._calendar.config.dateFormat;
		const value = this._cell.row[this._cell.col.id];
		if (!withoutSave) {
			if (value instanceof Date || calendarChange) {
				this._value = this._calendar.getValue();
				this._input.value = this._value;
			} else if (stringToDate(this._input.value, dateFormat, true) && this._input.value.length === value.length) {
				this._value = this._input.value;
			}
		}
		if (this._config.events.fire(GridEvents.beforeEditEnd, [this._value, this._cell.row, this._cell.col])) {
			this._input.removeEventListener("focus", this._handlers.onFocus);
			this._input.removeEventListener("change", this._handlers.onChange);
			this._handlers = {};
			this._popup.destructor();
			this._calendar.destructor();
			this._config.events.fire(GridEvents.afterEditEnd, [this._value, this._cell.row, this._cell.col]);
		} else {
			this._input.focus();
		}
	}
	toHTML() {
		const value = this._cell.row[this._cell.col.id];
		this._config.$editable.editor = this;
		return el("input.dhx_cell-editor.dhx_cell-editor__input.dhx_cell-editor__datepicker", {
			_hooks: {
				didInsert: this._handlers.didInsert
			},
			_key: "cell_editor",
			dhx_id: "cell_editor",
			value
		});
	}
	protected _initHandlers() {
		this._handlers = {
			onFocus: () => {
				awaitRedraw().then(() => {
					this._popup.show(this._input, { centering: true, mode: Position.bottom });
				});
			},
			onChange: () => {
				this.endEdit();
			},
			didInsert: (node: any) => {
				const input: HTMLInputElement = node.el;
				this._input = input;
				this._input.addEventListener("focus", this._handlers.onFocus);
				this._input.addEventListener("change", this._handlers.onChange);
				input.focus();
				input.setSelectionRange(input.value.length, input.value.length);
			}
		};
	}
}